import { useState } from "react";
import type { ReactNode } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, {
  AutocompleteProps,
  type AutocompleteCloseReason,
} from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

type SbuxGenericAutocompleteProps<T> = Omit<
  AutocompleteProps<T, false, false, false>,
  "renderInput"
> & {
  required?: boolean;
  slotProps?: {
    input?: {
      label?: ReactNode;
      required?: boolean;
      helperText?: string | null;
    };
  };
};

/**
 * Autocomplete component
 * @param {object} props  MUI Autocomplete component props
 */
const SbuxGenericAutocomplete = <T,>(
  props: SbuxGenericAutocompleteProps<T>,
) => {
  const [open, setOpen] = useState(false);

  const handleOpen = (event: React.SyntheticEvent) => {
    setOpen(true);
    typeof props.onOpen === "function" && props.onOpen(event);
  };

  const handleClose = (
    event: React.SyntheticEvent,
    reason: AutocompleteCloseReason,
  ) => {
    setOpen(false);
    typeof props.onClose === "function" && props.onClose(event, reason);
  };

  return (
    <Autocomplete
      sx={{
        "& input": {
          height: "2.0em",
          maxHeight: 32,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.slotProps?.input?.label}
          required={props.slotProps?.input?.required}
          helperText={props.slotProps?.input?.helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {props.loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...props}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
    />
  );
};

export default SbuxGenericAutocomplete;
